import { uniq }                       from 'lodash-es';
import { BuilderElement,
  BuilderElementOption,
  BuilderGroup,
  SelectedOptions,
  XAxisSeries,
  XAxisSeriesData,
  XAxisSeriesResponse }                                     from '@/views/x-axis-report/report-edit/types/types';
import { duplicateArr,
  generateLeadTimeRange,
  getDateRangeEachDay,
  getXAxisViewMode }                                     from '@/views/x-axis-report/report-edit/helpers/x-axis.report-general.helpers';
import { Elements, ElementType }      from '@/helpers/variables';
import { ChartPosition, ChartValues } from '@/components/chart/helpers/chart-variables';
import { ChartXAxis, Layout }         from '@/components/chart/chart.types';
import { BackendXAxisSeriesResponseType,
  XAxisSeriesType }                                     from '@/views/x-axis-report/report-edit/helpers/x-axis-report.variables';

import Legend = echarts.EChartOption.Legend;

export const runCallbackOnLayout = (layoutList: Layout[], callback: (layout: Layout) => void): void => {
  layoutList.forEach((layout: Layout) => {
    callback(layout);
  });
};

export const runCallbackOnCharts = (layoutList: Layout[], callback: (layout?: Layout) => void): void => {
  if (layoutList?.length) {
    runCallbackOnLayout(layoutList, (layout: Layout) => callback(layout));
  } else {
    callback();
  }
};

export const getXAxisData = (checkedOptions: SelectedOptions[], index: number): string[] => {
  let multiplier = 1;

  checkedOptions
    .forEach((el: SelectedOptions, i: number) => {
      if (i > 0 && checkedOptions[ index + i ]?.options) {
        multiplier *= checkedOptions[ index + i ].options.length;
      }
    });

  return duplicateArr(checkedOptions[ index ].options, multiplier);
};

export const getChartOffsetLeft = (series: XAxisSeries[]): number => {
  let multiplier = 1;
  const defaultYAxisOffset = 55;

  const highestValue: number = series
    .map((series: XAxisSeries) => series.data)
    .flat(2)
    .reduce((acc: number, curr: XAxisSeriesData) => {
      if (curr.value != null && curr.value > acc) {
        return curr.value;
      }
      return acc;
    }, 0);

  switch (true) {
  case highestValue >= 1000 && highestValue <= 10000:
    multiplier = 1.5;
    break;
  case highestValue > 10000:
    multiplier = 2;
    break;
  default:
    multiplier = 1;
  }

  return defaultYAxisOffset * multiplier;
};

const getSelectedOptions = (acc: SelectedOptions[], curr: BuilderElement): SelectedOptions[] => {
  let elementDateRange: string[] = [];

  const options: string[] = curr.options
    ?.reduce((prev: string[], next: BuilderElementOption) => {
      if (next.isSelected) {
        return next.label ? [ ...prev, next.label ] : [ ...prev, next.name ];
      }
      return prev;
    }, []) || [];

  if ((curr.type !== ElementType.VALUE && curr.value?.from && curr.value?.to) || curr.columnName === Elements.LEAD_TIME) {
    elementDateRange = curr.columnName === Elements.LEAD_TIME
      ? generateLeadTimeRange(curr.value?.from || '', curr.value?.to || '')
      : getDateRangeEachDay(curr.value?.from || '', curr.value?.to || '');
  }

  return [
    ...acc,
    {
      order: curr.order,
      options: elementDateRange.length ? elementDateRange : options,
    },
  ];
};

export const generateXAxis = (group: BuilderGroup<BuilderElement[]>): ChartXAxis[] => {
  const checkedOptions: SelectedOptions[] = group.data
    .reduce(getSelectedOptions, [])
    .filter((data: SelectedOptions) => data.options && data.options.length);

  return checkedOptions
    .map((data: SelectedOptions, index: number) => ({
      name: '',
      nameLocation: ChartPosition.START,
      nameTextStyle: {
        fontWeight: ChartValues.BOLD,
      },
      position: ChartPosition.BOTTOM,
      offset: (index + 1) * 30,
      axisLine: {
        onZero: false,
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        inside: true,
      },
      type: ChartValues.CATEGORY,
      data: getXAxisData(checkedOptions, index),
    }));
};

export const generateChartSeries = (series: XAxisSeriesResponse[]): XAxisSeries[] => {
  const isInPreviewMode: boolean = getXAxisViewMode() === 'vXAxisReportPreview';

  return series.map((value: XAxisSeriesResponse) => ({
    name: value.name,
    stack: value.function,
    type: value.type === BackendXAxisSeriesResponseType.COLUMN ? XAxisSeriesType.BAR : XAxisSeriesType.LINE,
    itemStyle: {
      color: isInPreviewMode ? value.color : null,
    },
    lineStyle: {
      color: isInPreviewMode ? value.color : null,
    },
    data: value.data.map((el: number) => ({ name: value.function, value: el })),
  }));
};

export const generateChartLegend = (series: XAxisSeriesResponse[]): Legend.LegendDataObject[] => uniq(
  series
    .map((value: XAxisSeriesResponse) => ({
      name: value.name,
      icon: ChartValues.CIRCLE,
    })),
)
  .filter((el: { name: string; icon: ChartValues }) => el.name);
